import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, FileText, Download, Ship, Plane, Filter, DollarSign, Calendar } from "lucide-react";
import html2pdf from "html2pdf.js";

export default function InformeResumen() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  // Filtros
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [tipoEnvio, setTipoEnvio] = useState("ambos");
  const [mostrarDatosEconomicos, setMostrarDatosEconomicos] = useState(false);
  const [logos, setLogos] = useState([]);
  
  // Resultados
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    fetchFilterData();
  }, []);

  // Preseleccionar Invermatis y LG Global por defecto
  useEffect(() => {
    if (suppliers.length > 0 && selectedSuppliers.length === 0) {
      const defaultSuppliers = suppliers
        .filter(s => 
          s.nombre.toLowerCase().includes("invermatis") || 
          s.nombre.toLowerCase().includes("lg global")
        )
        .map(s => s.id);
      setSelectedSuppliers(defaultSuppliers);
    }
  }, [suppliers]);

  const fetchFilterData = async () => {
    try {
      const [suppliersRes, productsRes, configRes] = await Promise.all([
        api.get("/suppliers"),
        api.get("/products"),
        api.get("/config"),
      ]);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
      setLogos(configRes.data.logos || []);
    } catch (error) {
      toast.error("Error cargando datos de filtros");
    }
  };

  const handleSupplierToggle = (supplierId) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleProductToggle = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllSuppliers = () => {
    if (selectedSuppliers.length === suppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(suppliers.map(s => s.id));
    }
  };

  const selectAllProducts = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    if (year && year !== "custom") {
      setFechaDesde(`${year}-01-01`);
      setFechaHasta(`${year}-12-31`);
    } else {
      setFechaDesde("");
      setFechaHasta("");
    }
  };

  const handleGenerarInforme = async () => {
    if (selectedSuppliers.length === 0) {
      toast.error("Selecciona al menos un proveedor");
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      toast.error("Indica las fechas del informe");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/reports/informe-resumen", {
        proveedor_ids: selectedSuppliers,
        producto_ids: selectedProducts.length > 0 ? selectedProducts : null,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        tipo_envio: tipoEnvio === "ambos" ? null : tipoEnvio,
        incluir_datos_economicos: mostrarDatosEconomicos,
      });
      setResultados(response.data);
      setShowResults(true);
      
      if (!response.data.bloques || response.data.bloques.length === 0) {
        toast.info("No se encontraron lotes con los filtros indicados");
      }
    } catch (error) {
      toast.error("Error generando el informe");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      if (dateStr.includes("-")) {
        const [y, m, d] = dateStr.split("-");
        return `${d}/${m}/${y}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value);
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value) + " $";
  };

  // Obtener nombre corto para PDF
  const getShortNameForPdf = (nombre) => {
    if (nombre.toLowerCase().includes("invermatis")) return "Invermatis";
    if (nombre.toLowerCase().includes("lg global")) return "LG Global";
    if (nombre.toLowerCase().includes("frisur")) return "Frisur";
    return nombre.split(",")[0].split(" ").slice(0, 2).join(" ");
  };

  // Obtener texto de filtros para pie de página
  const getFiltersSummary = () => {
    const provNames = selectedSuppliers.length === suppliers.length
      ? "Todos los proveedores"
      : suppliers.filter(s => selectedSuppliers.includes(s.id)).map(s => getShortNameForPdf(s.nombre)).join(", ");
    const tipoText = tipoEnvio === "maritimo" ? "Marítimo" : tipoEnvio === "aereo" ? "Aéreo" : "Todos";
    const economico = mostrarDatosEconomicos ? "Con datos económicos" : "Sin datos económicos";
    return `Proveedores: ${provNames} | Tipo: ${tipoText} | ${economico} | Período: ${formatDate(fechaDesde)} - ${formatDate(fechaHasta)}`;
  };

  const handleDownloadPdf = async () => {
    const element = document.getElementById("informe-resumen-container");
    if (!element) return;

    const fechaHoy = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const proveedoresNombres = selectedSuppliers.length === suppliers.length
      ? "TODOS"
      : suppliers
          .filter(s => selectedSuppliers.includes(s.id))
          .map(s => getShortNameForPdf(s.nombre))
          .join(" ");
    const rangoFechas = `${fechaDesde.replace(/-/g, "")}-${fechaHasta.replace(/-/g, "")}`;
    const filename = `${fechaHoy} Resumen ${proveedoresNombres} ${rangoFechas}`;

    const opt = {
      margin: [5, 5, 12, 5], // Aumentar margen inferior para pie de página
      filename: `${filename}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' }
    };

    try {
      const pdf = await html2pdf().set(opt).from(element).toPdf().get('pdf');
      const totalPages = pdf.internal.getNumberOfPages();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const fechaInforme = new Date().toLocaleDateString('es-ES');
      const filterText = getFiltersSummary();
      
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(100);
        const footerText = `Informe Resumen | ${filterText} | Generado: ${fechaInforme} | Página ${i}/${totalPages}`;
        pdf.text(footerText, pageWidth / 2, pageHeight - 4, { align: 'center' });
      }
      
      pdf.save(`${filename}.pdf`);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error generando el PDF");
    }
  };

  // Obtener logo de proveedor
  const getSupplierLogo = (supplierId) => {
    const logoConfig = logos.find(l => l.entidad_id === supplierId && l.tipo === 'proveedor');
    const baseUrl = BACKEND_URL;
    return logoConfig ? `${baseUrl}${logoConfig.url}` : null;
  };

  // Obtener nombre corto del proveedor
  const getSupplierShortName = (nombre) => {
    if (nombre.toLowerCase().includes("invermatis")) return "INVERMATIS";
    if (nombre.toLowerCase().includes("lg global")) return "LG GLOBAL";
    return nombre.toUpperCase().split(",")[0].split(" ").slice(0, 2).join(" ");
  };

  // Obtener logos de proveedores seleccionados para la cabecera
  const getSelectedSuppliersLogos = () => {
    const suppliersToShow = suppliers.filter(s => selectedSuppliers.includes(s.id));
    const baseUrl = BACKEND_URL;
    
    return suppliersToShow.map(s => {
      const logoConfig = logos.find(l => l.entidad_id === s.id && l.tipo === 'proveedor');
      return {
        nombre: s.nombre,
        logo: logoConfig ? `${baseUrl}${logoConfig.url}` : null
      };
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reports")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Informe Resumen</h1>
            <p className="text-slate-500 mt-1">Resumen de embarques por proveedor y tipo de envío</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      {!showResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Configurar Informe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Proveedores */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Proveedores</Label>
                <Button variant="outline" size="sm" onClick={selectAllSuppliers}>
                  {selectedSuppliers.length === suppliers.length ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
                {suppliers.map((supplier) => {
                  const logoUrl = getSupplierLogo(supplier.id);
                  return (
                    <div 
                      key={supplier.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-all ${
                        selectedSuppliers.includes(supplier.id) 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleSupplierToggle(supplier.id)}
                    >
                      <Checkbox
                        id={`supplier-${supplier.id}`}
                        checked={selectedSuppliers.includes(supplier.id)}
                        onCheckedChange={() => handleSupplierToggle(supplier.id)}
                      />
                      {logoUrl ? (
                        <img src={logoUrl} alt={supplier.nombre} className="h-8 w-auto object-contain" />
                      ) : (
                        <div className="h-8 w-8 bg-slate-200 rounded flex items-center justify-center text-xs font-bold text-slate-500">
                          {supplier.nombre?.charAt(0) || "P"}
                        </div>
                      )}
                      <Label htmlFor={`supplier-${supplier.id}`} className="text-sm cursor-pointer flex-1">
                        {supplier.nombre}
                      </Label>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">{selectedSuppliers.length} de {suppliers.length} seleccionados</p>
            </div>

            {/* Productos */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Productos (opcional)</Label>
                <Button variant="outline" size="sm" onClick={selectAllProducts}>
                  {selectedProducts.length === products.length ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-4 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all text-sm ${
                      selectedProducts.includes(product.id) 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleProductToggle(product.id)}
                  >
                    <Checkbox
                      id={`product-${product.id}`}
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={() => handleProductToggle(product.id)}
                    />
                    <Label htmlFor={`product-${product.id}`} className="text-xs cursor-pointer flex-1 truncate">
                      {product.nombre}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {selectedProducts.length === 0 
                  ? "Sin filtro de producto (todos)" 
                  : `${selectedProducts.length} de ${products.length} seleccionados`}
              </p>
            </div>

            {/* Fechas */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Período del informe</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Selección rápida</Label>
                  <Select value={selectedYear} onValueChange={handleYearSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar año..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Personalizado</SelectItem>
                      {availableYears.map((year) => (
                        <SelectItem key={year} value={year.toString()}>Año {year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Fecha desde</Label>
                  <Input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => { setFechaDesde(e.target.value); setSelectedYear("custom"); }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Fecha hasta</Label>
                  <Input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => { setFechaHasta(e.target.value); setSelectedYear("custom"); }}
                  />
                </div>
              </div>
            </div>

            {/* Tipo de envío */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Tipo de envío</Label>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox id="tipo-ambos" checked={tipoEnvio === "ambos"} onCheckedChange={() => setTipoEnvio("ambos")} />
                  <Label htmlFor="tipo-ambos" className="cursor-pointer">Ambos</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="tipo-maritimo" checked={tipoEnvio === "maritimo"} onCheckedChange={() => setTipoEnvio("maritimo")} />
                  <Label htmlFor="tipo-maritimo" className="cursor-pointer flex items-center gap-1">
                    <Ship className="w-4 h-4" /> Marítimo
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="tipo-aereo" checked={tipoEnvio === "aereo"} onCheckedChange={() => setTipoEnvio("aereo")} />
                  <Label htmlFor="tipo-aereo" className="cursor-pointer flex items-center gap-1">
                    <Plane className="w-4 h-4" /> Aéreo
                  </Label>
                </div>
              </div>
            </div>

            {/* Datos económicos */}
            <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="datos-economicos"
                  checked={mostrarDatosEconomicos}
                  onCheckedChange={(checked) => setMostrarDatosEconomicos(checked)}
                />
                <Label htmlFor="datos-economicos" className="cursor-pointer flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">Incluir datos de facturación</span>
                </Label>
              </div>
              <p className="text-xs text-amber-700 ml-7">Se añadirá la columna "Factura" con el importe en USD</p>
            </div>

            {/* Botón generar */}
            <div className="flex justify-end pt-4">
              <Button onClick={handleGenerarInforme} disabled={loading} className="bg-slate-900 hover:bg-slate-800">
                {loading ? (
                  <><div className="spinner w-4 h-4 mr-2" />Generando...</>
                ) : (
                  <><FileText className="w-4 h-4 mr-2" />Generar Informe</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {showResults && resultados && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => setShowResults(false)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Modificar filtros
            </Button>
            <Button onClick={handleDownloadPdf} className="bg-emerald-600 hover:bg-emerald-700">
              <Download className="w-4 h-4 mr-2" />Descargar PDF
            </Button>
          </div>

          <div id="informe-resumen-container" className="bg-white p-4 rounded-lg border text-xs">
            {/* Cabecera con logos */}
            <div className="mb-4 border-b pb-3">
              {/* Logos de proveedores */}
              <div className="flex items-center gap-4 mb-3 flex-wrap justify-center">
                {getSelectedSuppliersLogos().map((supplier, idx) => (
                  <div key={idx} className="flex items-center">
                    {supplier.logo ? (
                      <img 
                        src={supplier.logo} 
                        alt={supplier.nombre}
                        crossOrigin="anonymous"
                        className="h-12 object-contain max-w-[150px]"
                        onError={(e) => {
                          // Si falla la carga del logo, mostrar el nombre
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="h-12 px-4 bg-slate-100 rounded items-center justify-center text-sm font-medium text-slate-600"
                      style={{ display: supplier.logo ? 'none' : 'flex' }}
                    >
                      {supplier.nombre}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Título principal */}
              <h1 className="text-center text-sm font-bold text-blue-800">
                RESUMEN EMBARQUES {tipoEnvio === "maritimo" ? "MARÍTIMOS" : tipoEnvio === "aereo" ? "AÉREOS" : "AÉREOS Y MARÍTIMOS"}{" "}
                {selectedSuppliers.map(id => getSupplierShortName(suppliers.find(s => s.id === id)?.nombre || "")).join(" E ")}
              </h1>
            </div>

            {/* Bloques por proveedor/tipo */}
            {resultados.bloques?.map((bloque, idx) => (
              <div key={idx} className="mb-4">
                <table className="w-full border-collapse border border-slate-400 mb-2">
                  <thead>
                    <tr className="bg-blue-200">
                      <th colSpan={mostrarDatosEconomicos ? 8 : 7} className="border border-slate-400 p-1 text-center text-xs font-bold">
                        SEGUIMIENTO DE EMBARQUES {bloque.tipo_envio === "maritimo" ? "MARITIMOS" : "AEREOS"} {bloque.proveedor_nombre_corto}
                      </th>
                    </tr>
                    <tr className="bg-blue-100">
                      <th className="border border-slate-400 p-1 text-center w-10">Nro.</th>
                      <th className="border border-slate-400 p-1 text-center">FECHA DE RECONOCIMIENTO</th>
                      <th className="border border-slate-400 p-1 text-center">DESCRIPCIÓN</th>
                      <th className="border border-slate-400 p-1 text-center">
                        {bloque.tipo_envio === "maritimo" ? "CONTENEDOR" : "GUIA"}
                      </th>
                      <th className="border border-slate-400 p-1 text-center bg-green-200">PESO NETO</th>
                      <th className="border border-slate-400 p-1 text-center">
                        SALIDA DEL {bloque.tipo_envio === "maritimo" ? "BUQUE" : "VUELO"}
                      </th>
                      <th className="border border-slate-400 p-1 text-center">LLEGADA ESTIMADA</th>
                      {mostrarDatosEconomicos && (
                        <th className="border border-slate-400 p-1 text-center bg-amber-100">FACTURA $</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {bloque.lotes.map((lote, loteIdx) => (
                      <tr key={loteIdx} className={loteIdx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="border border-slate-400 p-1 text-center">{loteIdx + 1}</td>
                        <td className="border border-slate-400 p-1 text-center">{formatDate(lote.fecha_reconocimiento)}</td>
                        <td className="border border-slate-400 p-1 text-center">{lote.producto_nombre}</td>
                        <td className="border border-slate-400 p-1 text-center">
                          {bloque.tipo_envio === "maritimo" ? lote.contenedor : lote.awb}
                        </td>
                        <td className="border border-slate-400 p-1 text-center bg-green-50">{formatNumber(lote.peso_neto)}</td>
                        <td className="border border-slate-400 p-1 text-center">{formatDate(lote.fecha_salida)}</td>
                        <td className="border border-slate-400 p-1 text-center">{formatDate(lote.llegada_estimada)}</td>
                        {mostrarDatosEconomicos && (
                          <td className="border border-slate-400 p-1 text-center bg-amber-50">{formatNumber(lote.importe_factura_usd)}</td>
                        )}
                      </tr>
                    ))}
                    {/* Total del bloque */}
                    <tr className="bg-blue-100 font-bold">
                      <td colSpan={4} className="border border-slate-400 p-1 text-right">TOTAL</td>
                      <td className="border border-slate-400 p-1 text-center bg-green-200">{formatNumber(bloque.total_peso_neto)}</td>
                      <td colSpan={mostrarDatosEconomicos ? 3 : 2} className="border border-slate-400 p-1"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}

            {/* Tabla de totales */}
            {resultados.totales && (
              <div className="mt-6">
                <table className="border-collapse border border-slate-400">
                  <thead>
                    <tr className="bg-blue-200">
                      <th colSpan={2} className="border border-slate-400 p-1 text-center font-bold">TOTALES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.totales.por_bloque?.map((total, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="border border-slate-400 p-1 px-2">{total.nombre}</td>
                        <td className="border border-slate-400 p-1 px-2 text-right">{formatNumber(total.peso_neto)}</td>
                      </tr>
                    ))}
                    {/* Totales por proveedor */}
                    {resultados.totales.por_proveedor?.map((total, idx) => (
                      <tr key={`prov-${idx}`} className="bg-blue-50 font-semibold">
                        <td className="border border-slate-400 p-1 px-2">TOTAL {total.nombre}</td>
                        <td className="border border-slate-400 p-1 px-2 text-right">{formatNumber(total.peso_neto)}</td>
                      </tr>
                    ))}
                    {/* Total general */}
                    <tr className="bg-green-200 font-bold">
                      <td className="border border-slate-400 p-1 px-2">TOTAL GENERAL</td>
                      <td className="border border-slate-400 p-1 px-2 text-right">{formatNumber(resultados.totales.total_general)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
